import type { ProblemTemplate } from '../../types.ts';
import { rat } from '../../math/rational.ts';
import {
  binomial,
  choose,
  exactDiv,
  integerProblem,
  ipow,
  lcm,
  leadTerm,
  letter,
  linear,
  nextConst,
  nextTerm,
  scaled,
  sharingFactorWith,
  signedBlank,
} from './helpers-g09-10.ts';

/**
 * Grade 9 templates (CAPS Senior Phase, last year): products of binomials,
 * common factors, equations with fractions, exponent laws including negative
 * exponents, and the general term of a linear pattern.
 *
 * Ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 * Every answer is a whole number. Every template sets `allowNegative`: even
 * where the answer is always positive, a negative choice is a genuine
 * sign-slip misconception at this level.
 */

/** `(x + a)(x + b) = x² + □x + ab`, a, b > 0. */
const expandBinomial: ProblemTemplate = {
  id: 'g9.expand-binomial',
  grade: 9,
  title: '(x + a)(x + b): middle coefficient',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, d) {
    const hi = scaled(d, 5, 9);
    const a = rng.int(1, hi);
    const b = rng.intExcept(1, hi, [a]); // a = b is a perfect square — that is g9.square-expand
    const v = letter(rng, d);
    return integerProblem(`${binomial(1, v, a)}${binomial(1, v, b)} = ${v}^2 + \\box ${v} + ${a * b}`, a + b, [
      a * b, // multiplied a and b for the middle term (mixed up middle and last terms)
      a, // kept only one cross term (forgot the inner product)
      b, // kept only one cross term (forgot the outer product)
    ]);
  },
};

/**
 * `(x + p)(x + q) = x² ± □x + pq` with at least one of p, q negative. A negative
 * middle coefficient is shown as `x² - □x`, so the box always holds its size.
 */
const expandBinomialNeg: ProblemTemplate = {
  id: 'g9.expand-binomial-neg',
  grade: 9,
  title: '(x − a)(x + b): middle coefficient with negatives',
  parTimeMs: 16000,
  allowNegative: true,
  generate(rng, d) {
    const hi = scaled(d, 5, 9);
    const pm = rng.int(1, hi);
    const qm = rng.intExcept(1, hi, [pm]); // distinct sizes: no zero middle term, no perfect square
    let p = pm;
    let q = qm;
    if (d >= 0.5 && rng.chance(0.35)) {
      p = -pm;
      q = -qm;
    } else if (rng.chance(0.5)) p = -pm;
    else q = -qm;
    const v = letter(rng, d);
    const blank = signedBlank(p + q);
    return integerProblem(
      `${binomial(1, v, p)}${binomial(1, v, q)} = ${v}^2${blank.sign}\\box ${v}${nextConst(p * q)}`,
      blank.answer,
      [
        blank.fill(-(p + q)), // sign slip on the whole middle term
        blank.fill(p - q), // dropped the sign of one factor…
        blank.fill(q - p), // …or of the other
        blank.fill(p * q), // multiplied instead of adding (mixed up middle and last terms)
      ],
    );
  },
};

/** Take out a common factor and fill the term left inside the bracket. */
const factorCommon: ProblemTemplate = {
  id: 'g9.factor-common',
  grade: 9,
  title: 'Common factor: fill the bracket',
  parTimeMs: 12000,
  allowNegative: true,
  generate(rng, d) {
    const k = rng.int(2, scaled(d, 5, 9));
    const b = rng.int(1, scaled(d, 3, 6));
    const cHi = scaled(d, 5, 9);
    // c coprime to b so k really is the highest common factor; c ≠ k so "copy the factor" is never right by luck.
    const c = rng.intExcept(1, cHi, [...sharingFactorWith(b, 1, cHi), k]);
    const v = letter(rng, d);
    const inner = leadTerm(b, v);
    const form = choose(rng, [
      ['plus', 3],
      ['minus', d >= 0.35 ? 2 : 0],
      ['variable', d >= 0.5 ? 2 : 0],
      ['negative', d >= 0.7 ? 2 : 0],
    ] as const);

    let prompt: string;
    switch (form) {
      case 'plus': // 6x + 9 = 3(2x + □)
        prompt = `${linear(k * b, v, k * c)} = ${k}(${inner} + \\box)`;
        break;
      case 'minus': // 6x - 9 = 3(2x - □)
        prompt = `${linear(k * b, v, -k * c)} = ${k}(${inner} - \\box)`;
        break;
      case 'variable': // 6x² + 9x = 3x(2x + □)
        prompt = `${leadTerm(k * b, `${v}^2`)}${nextTerm(k * c, v)} = ${k}${v}(${inner} + \\box)`;
        break;
      case 'negative': // -6x + 9 = -3(2x - □) → 3: taking out a negative factor flips the sign inside
        prompt = `${linear(-k * b, v, k * c)} = -${k}(${inner} - \\box)`;
        break;
    }
    // Every form writes the bracket's sign in front of □, so the box always holds c itself.
    return integerProblem(prompt, c, [
      c * k, // forgot to divide the term by the common factor (copied it into the bracket)
      k, // wrote the common factor again inside the bracket
      -c, // sign slip (for a negative factor: forgot that taking it out flips the sign inside)
      k * c - k, // subtracted the factor instead of dividing by it
      c === 1 ? 0 : null, // "taking 3 out of 3 leaves nothing" — forgot the 1 that stays behind
    ]);
  },
};

/** `x/a ± x/b = c` with a whole-number solution. */
const eqFractions: ProblemTemplate = {
  id: 'g9.eq-fractions',
  grade: 9,
  title: 'Equation with two fractions',
  parTimeMs: 20000,
  allowNegative: true,
  generate(rng, d) {
    const denHi = scaled(d, 4, 8);
    let a = rng.int(2, denHi);
    let b = rng.intExcept(2, denHi, [a]);
    const minus = d >= 0.5 && rng.chance(0.4);
    if (minus && a > b) [a, b] = [b, a]; // x/a − x/b > 0 needs a < b
    const l = lcm(a, b);
    const m = rng.int(1, Math.max(1, Math.floor(scaled(d, 24, 120) / l)));
    const x = l * m;
    const c = minus ? x / a - x / b : x / a + x / b;
    const v = letter(rng, d);
    const prompt = `\\frac{${v}}{${a}} ${minus ? '-' : '+'} \\frac{${v}}{${b}} = ${c}`;
    return integerProblem(
      prompt,
      x,
      [
        c * a, // solved only the first fraction (x/a = c)
        c * b, // solved only the second fraction (x/b = c)
        minus ? c * (b - a) : c * (a + b), // combined the denominators: x/a ± x/b = x/(b − a) or x/(a + b)
        minus ? null : exactDiv(c * (a + b), 2), // added tops and bottoms: x/a + x/b = 2x/(a + b)
        exactDiv(c, minus ? l / a - l / b : l / a + l / b), // multiplied the left by the LCD but not the right
        minus ? exactDiv(c * a * b, a + b) : null, // treated the minus as a plus
      ],
      { key: 'solve', params: { v } },
    );
  },
};

/** `b^{-n} = 1/□` (and, higher up, `1/b^{-n} = □`). */
const expNegative: ProblemTemplate = {
  id: 'g9.exp-negative',
  grade: 9,
  title: 'Negative exponents',
  parTimeMs: 10000,
  allowNegative: true,
  generate(rng, d) {
    const baseHi = scaled(d, 5, 10);
    const cap = scaled(d, 32, 256);
    // n ≥ 2: with b^{-1} = 1/b the answer would just echo the base on show.
    const pairs: (readonly [number, number])[] = [];
    for (let b = 2; b <= baseHi; b++) for (let n = 2; ipow(b, n) <= cap; n++) pairs.push([b, n]);
    const [b, n] = rng.pick(pairs);
    const value = ipow(b, n);
    const reciprocal = d >= 0.5 && rng.chance(0.35);
    const prompt = reciprocal ? `\\frac{1}{${b}^{-${n}}} = \\box` : `${b}^{-${n}} = \\frac{1}{\\box}`;
    return integerProblem(prompt, value, [
      -value, // "a negative exponent makes the number negative"
      b * n, // multiplied the base by the exponent
      -b * n, // multiplied the base by the (negative) exponent
      ipow(n, b) <= 999 ? ipow(n, b) : null, // swapped base and exponent
      ipow(b, n - 1), // one factor short in the repeated multiplication
      // b^{-n} = 1/□: wrote the value 1/bⁿ instead of the missing denominator;
      // 1/b^{-n} = □: did not flip, left it as 1/bⁿ.
      rat(1, value),
    ]);
  },
};

/** Combined exponent laws, e.g. `a^7·a^2 / a^4 = a^□`. */
const expMixed: ProblemTemplate = {
  id: 'g9.exp-mixed',
  grade: 9,
  title: 'Combined exponent laws',
  parTimeMs: 12000,
  allowNegative: true,
  generate(rng, d) {
    const e = scaled(d, 4, 9); // largest base exponent: small at the gentle end (answers ≤ 16)
    const powHi = scaled(d, 3, 4);
    const nonPositive = d >= 0.6; // a^0 and a^{-n} results only at the top of the range
    const v = rng.pick(['a', 'x', 'm', 'y', 'k'] as const);
    const box = `${v}^{\\box}`;
    const form = choose(rng, [
      ['product-over', 3],
      ['power-times', 2],
      ['power-over', d >= 0.3 ? 2 : 0],
      ['power-of-product', d >= 0.6 ? 2 : 0],
    ] as const);
    const small = (x: number) => (x <= 99 ? x : null); // keep "raised the exponent" distractors believable

    switch (form) {
      case 'product-over': {
        const p = rng.int(2, e);
        const q = rng.int(2, e);
        // r ∉ {p, q, (p + q)/2}: otherwise the answer is a copy of an exponent already on show.
        const r = rng.intExcept(2, Math.min(12, nonPositive ? p + q + 2 : p + q - 1), [p, q, (p + q) / 2]);
        return integerProblem(`\\frac{${v}^{${p}} \\cdot ${v}^{${q}}}{${v}^{${r}}} = ${box}`, p + q - r, [
          p * q - r, // multiplied exponents when multiplying powers
          p + q + r, // added the exponent when dividing
          p + q, // ignored the denominator
        ]);
      }
      case 'power-times': {
        const p = rng.int(2, e);
        const q = rng.int(2, powHi);
        const r = rng.int(2, e);
        return integerProblem(`(${v}^{${p}})^{${q}} \\cdot ${v}^{${r}} = ${box}`, p * q + r, [
          p + q + r, // added exponents for a power of a power
          small(ipow(p, q) + r), // raised the exponent to the power
          p * q * r, // multiplied every exponent
          (p + r) * q, // applied the outer power to the whole product
          p * q, // dropped the lone factor
        ]);
      }
      case 'power-over': {
        const p = rng.int(2, e);
        const q = rng.int(2, powHi);
        // As above: keep the answer pq − r different from every exponent on show.
        const rMax = Math.min(12, nonPositive ? p * q + 2 : p * q - 1);
        const r = rng.intExcept(2, rMax, [p * q - p, p * q - q, (p * q) / 2]);
        return integerProblem(`\\frac{(${v}^{${p}})^{${q}}}{${v}^{${r}}} = ${box}`, p * q - r, [
          p + q - r, // added exponents for a power of a power
          p * q + r, // added the exponent when dividing
          small(ipow(p, q) - r), // raised the exponent to the power
          (p - r) * q, // applied the outer power to the whole quotient
          p * q, // ignored the denominator
        ]);
      }
      case 'power-of-product': {
        const p = rng.int(2, Math.min(e, 6));
        const q = rng.int(2, Math.min(e, 6));
        const r = rng.int(2, 3);
        return integerProblem(`(${v}^{${p}} \\cdot ${v}^{${q}})^{${r}} = ${box}`, (p + q) * r, [
          p + q + r, // added the outer exponent instead of multiplying
          p * q * r, // multiplied the inner exponents instead of adding
          p + q * r, // applied the outer power to the last factor only
          p + q, // forgot the outer power
        ]);
      }
    }
  },
};

/** General term of a linear pattern: find T_k, or (higher up) which term has a given value. */
const linearPattern: ProblemTemplate = {
  id: 'g9.linear-pattern',
  grade: 9,
  title: 'Linear pattern T_n = an + b',
  parTimeMs: 10000,
  allowNegative: true,
  generate(rng, d) {
    const a = d >= 0.5 && rng.chance(0.35) ? -rng.int(2, 6) : rng.int(2, scaled(d, 4, 9));
    const bHi = scaled(d, 9, 20);
    const b = d >= 0.35 ? rng.int(-bHi, bHi) : rng.int(0, bHi);
    const k = rng.int(5, scaled(d, 10, 50));
    const rule = `T_n = ${linear(a, 'n', b)}`;
    const value = a * k + b;

    if (d >= 0.6 && rng.chance(0.3)) {
      // Which term equals `value`? Solve an + b = value for n.
      return integerProblem(
        `${rule} \\sep T_n = ${value}`,
        k,
        [
          value - b, // forgot to divide by the difference a
          exactDiv(value + b, a), // moved the constant with the wrong sign
          exactDiv(value, a), // ignored the constant
          k + 1, // off by one position
        ],
        { key: 'solve', params: { v: 'n' } },
      );
    }
    return integerProblem(
      rule,
      value,
      [
        a * k, // forgot the constant
        a * (k - 1) + b, // off by one position (counted from T_0)
        b * k + a, // swapped the difference and the constant
        b !== 0 ? a * k - b : null, // sign slip on the constant
      ],
      { key: 'term', params: { n: k } },
    );
  },
};

/** `(x ± a)²` and `(bx ± a)²`: fill the middle coefficient (or, as a variant, the constant). */
const squareExpand: ProblemTemplate = {
  id: 'g9.square-expand',
  grade: 9,
  title: 'Square of a binomial',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, d) {
    const v = letter(rng, d);
    const form = choose(rng, [
      ['plus', 3],
      ['minus', d >= 0.3 ? 3 : 0],
      ['constant', d >= 0.5 ? 2 : 0],
      ['coefficient', d >= 0.7 ? 3 : 0],
    ] as const);

    if (form === 'constant') {
      // (x - 7)² = x² - 14x + □ → 49
      const a = rng.int(2, scaled(d, 9, 12));
      const s = rng.sign();
      return integerProblem(`${binomial(1, v, s * a)}^2 = ${v}^2${nextTerm(2 * s * a, v)} + \\box`, a * a, [
        -a * a, // squared the sign wrongly: (−a)² = −a²
        2 * a, // doubled instead of squaring
        a, // did not square at all
      ]);
    }

    const b = form === 'coefficient' ? rng.int(2, 3) : 1;
    const a = rng.int(1, form === 'coefficient' ? 5 : scaled(d, 6, 9));
    const s = form === 'plus' ? 1 : form === 'minus' ? -1 : rng.sign();
    const middle = 2 * b * a * s;
    // (x - 6)² = x² - □x + 36 → 12: a negative middle term keeps its sign outside the box.
    const blank = signedBlank(middle);
    return integerProblem(`${binomial(b, v, s * a)}^2 = ${leadTerm(b * b, `${v}^2`)}${blank.sign}\\box ${v} + ${a * a}`, blank.answer, [
      blank.fill(middle / 2), // only one cross term (forgot to double)
      0, // forgot the middle term altogether: (x + a)² = x² + a²
      blank.fill(-middle), // sign slip
      blank.fill(s * a * a), // used the constant term's number
      b > 1 ? blank.fill(2 * a * s) : null, // forgot to multiply by x's coefficient
    ]);
  },
};

/** Grade 9 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE. */
export const GRADE_09: ProblemTemplate[] = [
  expandBinomial,
  expandBinomialNeg,
  factorCommon,
  eqFractions,
  expNegative,
  expMixed,
  linearPattern,
  squareExpand,
];
